import { Formations } from "@/api/privateRequests/squad-builder/get-formations";
import { cn } from "@/lib/utils";
import { topToPageSelector } from "@/redux/globalState/globalStateSlice";
import clsx from "clsx";
import { useSelector } from "react-redux";

import { APIStatus } from "../../../../api/APIStatus.type";
import { overviewIcon, tacticIcon } from "../../../../designSystemIcons";
import {
  LeftPanelMode,
  Squad,
  SquadBuilderMode,
} from "../../../../redux/squadBuilder/squadBuilderUtils";
import { Tactic } from "../../../../redux/tactics/tacticsSlice";
import { OnBoardingNumber } from "../../OnBoarding/OnBoardingNumber";
import { BestFormationMode } from "./BestFormationMode/BestFormationMode";
import { ChemistryOverviewMode } from "./ChemistryOverviewMode/ChemistryOverviewMode";
import { TacticMode } from "./TacticMode/TacticMode";

type Props = {
  squad: Squad;
  tempSquad: Squad;
  mode: SquadBuilderMode;
  desktopMode: boolean;
  formations: Formations;
  leftPanelMode: LeftPanelMode;
  formationLabel: string;
  onBoardingStep?: number;
  isShowingOnBoarding?: boolean;
  chosenTactic?: Tactic;
  handleSwitchLeftPanelMode?: (type: "overview" | "tactics") => void;
  handleCopy: () => void;
  handleChangeFormation?: (formationId: string) => void;
  handleChangeBuildUpStyle?: (buildUpStyle: string) => void;
  handleChangeDefensiveApproach?: (defensiveApproach: string) => void;
  handleChangeLineHeight?: (lineHeight: number) => void;
  handleGetTacticFromCode?: (code: string) => void;
  statusInsertTacticCode?: APIStatus;
  scrollPastTopBar: boolean;
};

type LeftPanelStickerProps = {
  type: "overview" | "tactics";
  onClick: () => void;
  isActive?: boolean;
};

const LeftPanelSticker = ({
  type,
  onClick,
  isActive,
}: LeftPanelStickerProps) => (
  <button
    className={clsx(
      "py-2 rounded-r-lg w-fit border-b border-r border-t border-white/20",
      isActive
        ? "bg-primary-blue pl-2 pr-2 text-white"
        : "hover:pl-4 pl-2 pr-2 bg-white/10 hover:bg-white/20 hover:text-white text-white/75",
      "transition-all duration-200 ease-in-out",
    )}
    onClick={onClick}
  >
    {type === "tactics" ? tacticIcon : overviewIcon}
  </button>
);

export const LeftPanel = ({
  squad,
  tempSquad,
  mode,
  desktopMode,
  formations,
  leftPanelMode,
  onBoardingStep,
  isShowingOnBoarding,
  chosenTactic,
  formationLabel,
  handleSwitchLeftPanelMode,
  handleCopy,
  handleChangeFormation,
  handleChangeBuildUpStyle,
  handleChangeDefensiveApproach,
  handleChangeLineHeight,
  handleGetTacticFromCode,
  statusInsertTacticCode,
  scrollPastTopBar,
}: Props) => {
  const topToPage = useSelector(topToPageSelector);
  return (
    <>
      {mode === "default" && (
        <div
          className={cn(
            "mt-30 z-30 touch-device:flex flex-col gap-y-2 hidden w-[50px]",
            scrollPastTopBar && desktopMode ? "fixed" : "absolute",
            scrollPastTopBar && desktopMode
              ? "desktop:left-[calc(max(0px,calc((100%-2000px)/2))+260px)] sb-v2-lg:left-[calc(max(0px,calc((100%-2000px)/2))+352px)]"
              : "desktop:left-[260px] sb-v2-lg:left-[352px]",
            scrollPastTopBar && desktopMode && topToPage.tailwindString,
          )}
        >
          <LeftPanelSticker
            type={"overview"}
            isActive={leftPanelMode !== "tactics"}
            onClick={() => handleSwitchLeftPanelMode("overview")}
          />
          <LeftPanelSticker
            type={"tactics"}
            isActive={leftPanelMode === "tactics"}
            onClick={() => handleSwitchLeftPanelMode("tactics")}
          />
        </div>
      )}
      <section
        className={clsx(
          "w-full desktop:w-[260px] sb-v2-lg:w-[352px] h-full desktop:bottom-0 min-h-screen mobile:min-h-50 bg-side-panel shadow-left-panel px-6 mobile:px-0 pb-6 mobile:pb-0",
          !isShowingOnBoarding && "z-21",
          scrollPastTopBar && desktopMode
            ? "fixed left-[max(0px,calc((100%-2000px)/2))]"
            : "absolute overflow-y-auto left-0",
          leftPanelMode === "tactics" && "right-0",
          scrollPastTopBar && desktopMode && topToPage.tailwindString,
        )}
      >
        {onBoardingStep === 1 && (
          <div className="absolute top-[460px] -right-4 sb-v2-lg:right-2 z-21">
            <OnBoardingNumber number={1} />
          </div>
        )}
        {leftPanelMode === "choose-formation" ? (
          <BestFormationMode />
        ) : leftPanelMode === "chem-overview" ? (
          <ChemistryOverviewMode
            squad={squad}
            tempSquad={tempSquad}
            mode={mode}
          />
        ) : leftPanelMode === "tactics" ? (
          <TacticMode
            formations={formations}
            formationLabel={formationLabel}
            chosenTactic={chosenTactic}
            mode="squad-builder"
            handleCopy={handleCopy}
            handleChangeBuildUpStyle={handleChangeBuildUpStyle}
            handleChangeDefensiveApproach={handleChangeDefensiveApproach}
            handleChangeFormation={handleChangeFormation}
            handleChangeLineHeight={handleChangeLineHeight}
            handleGetTacticFromCode={handleGetTacticFromCode}
            statusInsertTacticCode={statusInsertTacticCode}
            desktopMode={desktopMode}
          />
        ) : null}
      </section>
    </>
  );
};
